/**
 * AIGenerated - Mark extension for AI-generated content attribution
 *
 * Applies to text generated by AI, showing:
 * - Subtle violet underline (always visible)
 * - Hover tooltip with "AI wrote this"
 * - Optional model and timestamp metadata
 *
 * This enables transparency and trust in AI collaboration by clearly
 * marking which content was generated by AI vs written by humans.
 *
 * Usage:
 * editor.chain().focus().setAIGenerated({ model: 'claude' }).run()
 * editor.chain().focus().unsetAIGenerated().run()
 *
 * The mark persists in the Y.Doc and survives page refresh.
 * CSS styles are in editor.css under "AI GENERATED CONTENT ATTRIBUTION".
 *
 * Requirements implemented:
 * - GHOST-03: AI attribution system for transparency
 */

import { Mark, mergeAttributes } from '@tiptap/core';

// ============================================================================
// TIPTAP TYPE AUGMENTATION
// ============================================================================

/**
 * Extend Tiptap's Commands interface to add AI attribution commands.
 * This enables TypeScript autocomplete for editor.commands.setAIGenerated()
 */
declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    aiGenerated: {
      /**
       * Apply AI generated mark to current selection or inserted text.
       *
       * @param attrs - Optional attributes for the mark
       * @param attrs.model - The AI model that generated the text (e.g., 'claude', 'gemini')
       * @param attrs.generatedAt - ISO timestamp of when the text was generated
       * @example
       * // Mark selected text as AI-generated
       * editor.commands.setAIGenerated({ model: 'claude' });
       *
       * // Mark with custom timestamp
       * editor.commands.setAIGenerated({ generatedAt: '2026-01-24T10:00:00Z' });
       */
      setAIGenerated: (attrs?: { model?: string; generatedAt?: string }) => ReturnType;

      /**
       * Remove AI generated mark from current selection.
       * Use this to "humanize" AI-generated text after review/editing.
       *
       * @example
       * editor.commands.unsetAIGenerated();
       */
      unsetAIGenerated: () => ReturnType;
    };
  }
}

// ============================================================================
// EXTENSION
// ============================================================================

/**
 * AIGenerated Mark Extension
 *
 * Creates a mark that visually indicates AI-generated content.
 * The mark stores metadata (model, timestamp) and renders with
 * a violet underline and hover tooltip.
 *
 * @example
 * // In your editor configuration:
 * import { AIGenerated } from './extensions/ai-generated';
 *
 * const editor = useEditor({
 *   extensions: [
 *     AIGenerated,
 *   ],
 * });
 *
 * // Apply mark to text being inserted by AI
 * editor.chain().focus().setAIGenerated({ model: 'claude' }).run();
 * editor.chain().focus().insertText('AI generated text').run();
 * editor.chain().focus().unsetAIGenerated().run();
 */
export const AIGenerated = Mark.create({
  // Name of the mark - used in schema and commands
  name: 'aiGenerated',

  // -------------------------------------------------------------------------
  // DEFAULT OPTIONS
  // -------------------------------------------------------------------------

  addOptions() {
    return {
      // HTML attributes applied to all AI-generated spans
      // The class triggers CSS styling for underline and tooltip
      HTMLAttributes: {
        class: 'ai-generated',
        'data-ai-generated': 'true',
      },
    };
  },

  // -------------------------------------------------------------------------
  // MARK ATTRIBUTES
  // -------------------------------------------------------------------------

  /**
   * Define the attributes stored on the mark.
   * These are serialized to HTML and persisted in Y.Doc.
   *
   * - generatedAt: ISO timestamp for when the text was generated
   * - model: Name of the AI model (e.g., 'claude', 'gemini')
   */
  addAttributes() {
    return {
      // Timestamp of when the AI generated this text
      generatedAt: {
        default: null,
        // Parse from HTML data attribute when loading
        parseHTML: (element) => element.getAttribute('data-generated-at'),
        // Render to HTML data attribute when saving
        renderHTML: (attributes) => {
          if (!attributes.generatedAt) return {};
          return { 'data-generated-at': attributes.generatedAt };
        },
      },
      // The AI model that generated this text
      model: {
        default: null,
        // Parse from HTML data attribute when loading
        parseHTML: (element) => element.getAttribute('data-model'),
        // Render to HTML data attribute when saving
        renderHTML: (attributes) => {
          if (!attributes.model) return {};
          return { 'data-model': attributes.model };
        },
      },
    };
  },

  // -------------------------------------------------------------------------
  // HTML PARSING
  // -------------------------------------------------------------------------

  /**
   * When loading HTML content, recognize spans with data-ai-generated
   * attribute as AI-generated marks.
   */
  parseHTML() {
    return [
      {
        tag: 'span[data-ai-generated]',
      },
    ];
  },

  // -------------------------------------------------------------------------
  // HTML RENDERING
  // -------------------------------------------------------------------------

  /**
   * Render AI-generated marks as spans with merged attributes.
   * The '0' placeholder is where child content goes.
   */
  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ];
  },

  // -------------------------------------------------------------------------
  // COMMANDS
  // -------------------------------------------------------------------------

  addCommands() {
    return {
      /**
       * Apply AI-generated mark to current selection.
       *
       * If no generatedAt is provided, uses current timestamp.
       * This ensures every AI-generated text has a timestamp for audit.
       *
       * Usage patterns:
       * 1. Select existing text -> setAIGenerated() -> mark it as AI-generated
       * 2. setAIGenerated() -> insertText() -> unsetAIGenerated() -> mark inserted text
       */
      setAIGenerated:
        (attrs) =>
        ({ commands }) => {
          return commands.setMark(this.name, {
            // Auto-generate timestamp if not provided
            generatedAt: attrs?.generatedAt || new Date().toISOString(),
            model: attrs?.model,
          });
        },

      /**
       * Remove AI-generated mark from current selection.
       *
       * This allows users to "humanize" AI-generated text after
       * they've reviewed and edited it to their satisfaction.
       */
      unsetAIGenerated:
        () =>
        ({ commands }) => {
          return commands.unsetMark(this.name);
        },
    };
  },
});
